import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  DocumentTextIcon,
  FireIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import axios from 'axios';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const EnhancedDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchAnalytics();
    fetchUserInfo();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/dashboard/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Chart data for compliance
  const complianceData = analytics ? {
    labels: ['Compliant', 'In Progress', 'Not Started'],
    datasets: [{
      data: [
        analytics.compliance?.compliant_controls || 0,
        analytics.compliance?.controls - analytics.compliance?.compliant_controls || 0,
        analytics.compliance?.controls * 0.2 || 0 // Mock not started
      ],
      backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
      borderWidth: 0
    }]
  } : null;

  // Chart data for risks
  const riskData = analytics ? {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [{
      label: 'Risk Distribution',
      data: [
        analytics.risk?.critical_risks || 0,
        analytics.risk?.high_risks || 0,
        analytics.risk?.total_risks * 0.4 || 0, // Mock medium
        analytics.risk?.total_risks * 0.3 || 0  // Mock low
      ],
      backgroundColor: ['#DC2626', '#EA580C', '#D97706', '#65A30D']
    }]
  } : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.name?.split(' ')[0] || 'User'}! 👋
              </h1>
              <p className="text-gray-600 mt-2">
                Here's your compliance and risk management overview
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Last Login</div>
              <div className="font-medium">
                {user?.last_login ? new Date(user.last_login).toLocaleDateString() : 'First time'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-6 rounded-lg shadow-sm border"
            >
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-full">
                  <ShieldCheckIcon className="w-8 h-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Compliance Rate</p>
                  <p className="text-3xl font-bold text-green-600">
                    {analytics.compliance?.compliance_rate?.toFixed(1)}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {analytics.compliance?.compliant_controls}/{analytics.compliance?.controls} controls
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-6 rounded-lg shadow-sm border"
            >
              <div className="flex items-center">
                <div className="p-3 bg-orange-100 rounded-full">
                  <ExclamationTriangleIcon className="w-8 h-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Risk Score</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {analytics.risk?.risk_score?.toFixed(1)}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {analytics.risk?.critical_risks + analytics.risk?.high_risks} high/critical
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-6 rounded-lg shadow-sm border"
            >
              <div className="flex items-center">
                <div className="p-3 bg-red-100 rounded-full">
                  <FireIcon className="w-8 h-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Security Issues</p>
                  <p className="text-3xl font-bold text-red-600">
                    {analytics.security?.critical_vulnerabilities + analytics.security?.open_incidents || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Critical vulnerabilities + incidents
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white p-6 rounded-lg shadow-sm border"
            >
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-full">
                  <UserGroupIcon className="w-8 h-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Vendor Risk</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {analytics.vendors?.critical_vendors || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Critical vendor risks
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Compliance Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white p-6 rounded-lg shadow-sm border"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ShieldCheckIcon className="w-5 h-5 text-green-600 mr-2" />
              Compliance Status
            </h3>
            {complianceData && (
              <div className="h-64 flex items-center justify-center">
                <Doughnut 
                  data={complianceData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }}
                />
              </div>
            )}
          </motion.div>

          {/* Risk Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white p-6 rounded-lg shadow-sm border"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 mr-2" />
              Risk Distribution
            </h3>
            {riskData && (
              <div className="h-64">
                <Bar 
                  data={riskData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    },
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }}
                />
              </div>
            )}
          </motion.div>
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white p-6 rounded-lg shadow-sm border"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              {[
                { icon: ShieldCheckIcon, label: 'Upload Evidence', color: 'text-green-600' },
                { icon: ExclamationTriangleIcon, label: 'Add Risk', color: 'text-orange-600' },
                { icon: DocumentTextIcon, label: 'Generate Report', color: 'text-blue-600' },
                { icon: UserGroupIcon, label: 'Assess Vendor', color: 'text-purple-600' }
              ].map((action, index) => (
                <button
                  key={index}
                  className="w-full flex items-center p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <action.icon className={`w-5 h-5 ${action.color} mr-3`} />
                  <span className="text-gray-700">{action.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Framework Status */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="lg:col-span-2 bg-white p-6 rounded-lg shadow-sm border"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Frameworks</h3>
            <div className="space-y-4">
              {[
                { name: 'ISO/IEC 27001:2022', status: 'compliant', progress: 95, color: 'green' },
                { name: 'SOC 2 Type II', status: 'in_progress', progress: 78, color: 'yellow' },
                { name: 'GDPR', status: 'in_progress', progress: 85, color: 'yellow' }
              ].map((framework, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      framework.color === 'green' ? 'bg-green-500' : 'bg-yellow-500'
                    }`}></div>
                    <div>
                      <div className="font-medium text-gray-900">{framework.name}</div>
                      <div className={`text-sm ${
                        framework.status === 'compliant' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {framework.status === 'compliant' ? 'Compliant' : 'In Progress'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">{framework.progress}%</div>
                    <div className="w-20 bg-gray-200 rounded-full h-2 mt-1">
                      <div 
                        className={`h-2 rounded-full ${
                          framework.color === 'green' ? 'bg-green-500' : 'bg-yellow-500'
                        }`}
                        style={{ width: `${framework.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboard;